/**
 * Org selection + runtime resolution (MULTI-ORG-PORTAL plan). Replaces the
 * single-org `OrgScopeEnforcer` that force-pinned every session to
 * `VITE_WORKOS_ORG_ID`.
 *
 * Flow, per signed-in user:
 *   1. `/api/my-organizations` → the caller's active memberships.
 *      Fetch failure degrades to a one-entry list holding the legacy default
 *      org, which reproduces the old pinning exactly — a broken/undeployed
 *      API function must never brick the portal for the existing org.
 *   2. Target org = saved choice (localStorage) if still a membership,
 *      else the session's current org if a membership, else the sole
 *      membership, else render the picker.
 *   3. Session org ≠ target → AuthKit `switchToOrganization` (the webclient's
 *      flow; may hard-redirect under org auth policies, which is why the
 *      choice is persisted BEFORE switching).
 *   4. `get_org_info` on `{stamp}-platform` → `{slug, displayName}` — the
 *      slug every SpacetimeDB module name derives from (services/orgSession).
 *
 * Children render ONLY in the ready state, so `useOrgSession()` downstream is
 * total — SpacetimeProvider and every module-name derivation can assume a
 * resolved org. Unprovisioned orgs (no platform registry row) land in an
 * explicit error screen naming the org, with a way back to the picker —
 * never a guessed fallback (docs/platform-issues-2026-07-29.md).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { EXPECTED_ORG_ID, useAuthKit } from './AuthProvider';
import {
  resolveOrgSession,
  type OrgSession,
} from '../services/orgSession';
import { consumeUrlOrgIntent, orgIdFromUrlSegment } from '../services/orgFromUrl';

const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI as string;
const SELECTED_ORG_KEY = 'mt:selectedOrgId';
/** Loud local-dev pin, mirroring the studio's VITE_TURBOTWIN_ORG. Never set in CI. */
const DEV_ORG_OVERRIDE = import.meta.env.VITE_TURBOTWIN_ORG as string | undefined;

export type OrgMembershipInfo = { id: string; name: string; role: string };

type OrgState =
  | { kind: 'loading'; detail: string }
  | { kind: 'select'; organizations: OrgMembershipInfo[] }
  | { kind: 'ready'; org: OrgSession; organizations: OrgMembershipInfo[] }
  | { kind: 'error'; message: string; organizations: OrgMembershipInfo[] };

type OrgContextValue = {
  org: OrgSession;
  organizations: OrgMembershipInfo[];
  /** Persist a new choice and re-scope the session to it. */
  selectOrg: (orgId: string) => void;
};

const Ctx = createContext<OrgContextValue | null>(null);

/** The resolved org session. Total by construction — see module docblock. */
export function useOrgSession(): OrgContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error(
      'useOrgSession outside OrgProvider ready-state — the connected tree renders behind that gate, so this is a wiring bug',
    );
  }
  return v;
}

function readSavedOrg(): string | null {
  try {
    return localStorage.getItem(SELECTED_ORG_KEY);
  } catch {
    return null;
  }
}

function saveOrg(orgId: string): void {
  try {
    localStorage.setItem(SELECTED_ORG_KEY, orgId);
  } catch {
    // Storage unavailable — selection just won't persist across reloads.
  }
}

async function fetchMemberships(token: string): Promise<OrgMembershipInfo[]> {
  const resp = await fetch('/api/my-organizations', {
    headers: { 'X-Workos-Authorization': `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`my-organizations HTTP ${resp.status}`);
  const body = (await resp.json()) as { organizations?: OrgMembershipInfo[] };
  if (!Array.isArray(body.organizations)) throw new Error('malformed response');
  return body.organizations;
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user, organizationId, getAccessToken, switchToOrganization } = useAuthKit();
  const [state, setState] = useState<OrgState>({ kind: 'loading', detail: 'Loading your organizations…' });
  const [memberships, setMemberships] = useState<OrgMembershipInfo[] | null>(null);
  // One switch attempt per target org — a failed/looping silent switch must
  // surface as an error, not an infinite refresh (the 07-28 session-loop bug).
  const switchAttemptedFor = useRef<string | null>(null);

  // Step 1 — memberships, once per signed-in user.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const orgs = await fetchMemberships(token ?? '');
        if (cancelled) return;
        if (orgs.length === 0) {
          setState({
            kind: 'error',
            message:
              'Your account has no organization memberships. Ask an administrator to invite you to an organization.',
            organizations: [],
          });
          return;
        }
        // URL-driven switch: a path whose org segment names one of the user's
        // orgs is an explicit choice — persist it BEFORE the selection effect
        // runs, so it takes the saved-choice slot and the normal
        // switch/resolve machinery does the rest. The segment comes from the
        // sessionStorage stash, NOT the live pathname: sign-in hard-redirects
        // (Entra SSO for @minetwin.ai) drop the original path before this
        // effect ever runs (see services/orgFromUrl). Unknown segments change
        // nothing.
        const seg = consumeUrlOrgIntent();
        if (seg) {
          try {
            const fromUrl = await orgIdFromUrlSegment(seg, orgs, token ?? '', STDB_URI);
            if (cancelled) return;
            if (fromUrl) {
              saveOrg(fromUrl);
            } else {
              console.warn(`[org] URL segment "${seg}" matches none of your organizations — keeping the current selection`);
            }
          } catch (err) {
            console.warn('[org] URL org match failed — keeping the current selection:', err);
          }
        }
        setMemberships(orgs);
      } catch (err) {
        if (cancelled) return;
        // Degrade to exactly the legacy single-org behavior.
        console.warn(
          '[org] membership listing failed — falling back to the default org:',
          err,
        );
        setMemberships([{ id: EXPECTED_ORG_ID, name: 'MineTwin', role: 'member' }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, getAccessToken]);

  // Steps 2-4 — pick target, align the session, resolve the slug.
  useEffect(() => {
    if (!userId) return;

    // Loud dev pin (studio parity): skip listing/switching entirely.
    if (DEV_ORG_OVERRIDE) {
      console.warn(`[org] VITE_TURBOTWIN_ORG pins the org to "${DEV_ORG_OVERRIDE}"`);
      setState({
        kind: 'ready',
        org: {
          orgId: organizationId ?? 'unknown',
          slug: DEV_ORG_OVERRIDE,
          displayName: DEV_ORG_OVERRIDE,
        },
        organizations: [],
      });
      return;
    }

    if (!memberships) return;

    const saved = readSavedOrg();
    const target =
      (saved && memberships.find((m) => m.id === saved)?.id) ||
      (organizationId && memberships.find((m) => m.id === organizationId)?.id) ||
      (memberships.length === 1 ? memberships[0].id : null);

    if (!target) {
      setState({ kind: 'select', organizations: memberships });
      return;
    }

    if (organizationId !== target) {
      if (switchAttemptedFor.current === target) {
        setState({
          kind: 'error',
          message: `Could not scope your session to this organization (${target}). Sign out and back in, or pick a different organization.`,
          organizations: memberships,
        });
        return;
      }
      switchAttemptedFor.current = target;
      saveOrg(target);
      setState({ kind: 'loading', detail: 'Switching organization…' });
      switchToOrganization({ organizationId: target }).catch((err) => {
        setState({
          kind: 'error',
          message: `Organization switch failed: ${err instanceof Error ? err.message : String(err)}`,
          organizations: memberships,
        });
      });
      return;
    }

    // Session is scoped to the target — resolve slug + display name.
    let cancelled = false;
    setState({ kind: 'loading', detail: 'Resolving organization…' });
    (async () => {
      try {
        const token = await getAccessToken();
        const org = await resolveOrgSession(target, token ?? '', STDB_URI);
        if (!cancelled) setState({ kind: 'ready', org, organizations: memberships });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: `This organization isn't provisioned for the MineTwin portal yet (${err instanceof Error ? err.message : String(err)}).`,
            organizations: memberships,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, memberships, organizationId, getAccessToken, switchToOrganization]);

  const selectOrg = useCallback(
    (orgId: string) => {
      saveOrg(orgId);
      switchAttemptedFor.current = null;
      // The org-alignment effect re-runs off this state change and performs
      // the switch/resolve; memberships are already loaded.
      setState({ kind: 'loading', detail: 'Switching organization…' });
      setMemberships((m) => (m ? [...m] : m));
    },
    [],
  );

  if (state.kind === 'ready') {
    return (
      <Ctx.Provider value={{ org: state.org, organizations: state.organizations, selectOrg }}>
        {children}
      </Ctx.Provider>
    );
  }
  return <OrgGateScreen state={state} onSelect={selectOrg} />;
}

/** Full-window gate: loading / picker / error. Matches the connect screen. */
function OrgGateScreen({
  state,
  onSelect,
}: {
  state: Exclude<OrgState, { kind: 'ready' }>;
  onSelect: (orgId: string) => void;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--ink, #060E18)',
        color: 'var(--muted, #7A8499)',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {state.kind === 'loading' && <div>{state.detail}</div>}
      {(state.kind === 'select' || state.kind === 'error') && (
        <div
          style={{
            background: '#0B1622',
            border: '1px solid #1E2A3A',
            borderRadius: 12,
            padding: '28px 32px',
            width: 380,
            display: 'grid',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600, color: '#E6EDF3' }}>
            {state.kind === 'select' ? 'Choose an organization' : 'Organization unavailable'}
          </div>
          {state.kind === 'error' && (
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{state.message}</div>
          )}
          {state.organizations.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelect(o.id)}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid #2A3B52',
                background: 'transparent',
                color: '#E6EDF3',
                fontSize: 14,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              {o.name}
              <span style={{ color: '#7A8499', fontSize: 12 }}> — {o.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
